import { useState, useEffect } from "react";
import {
  Tab,
  TextField,
  BooleanField,
  FunctionField,
  ShowContextProvider,
  useRecordContext,
  Labeled,
  useDataProvider,
} from "react-admin";
import { makeStyles } from "@mui/styles";
import {
  Divider,
  Stack,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";

import { LanguageNameArrayField, AvailabilityField } from "../components";
import { OperationRadiusField, GeolocationField } from "../../../../components";

const styles = {
  inline: { display: "inline-block" },
  chip: { marginRight: 3 },
};
const useStyles = makeStyles(styles);

// Component to display provider's 90-day stats
const ProviderMatchingStats = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!record?.id) return;

    const fetchStats = async () => {
      try {
        setLoading(true);
        const { data } = await dataProvider.getList("Stats", {
          pagination: { page: 1, perPage: 1 },
          sort: { field: "firstName", order: "ASC" },
          filter: { periodDays: 90, providerId: record.id },
        });
        if (data && data.length > 0) {
          setStats(data[0]);
        }
      } catch (error) {
        console.error("Failed to fetch provider stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [record?.id, dataProvider]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading stats...</Typography>
      </Box>
    );
  }

  if (!stats) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No stats available for this provider
      </Typography>
    );
  }

  return (
    <Stack sx={{ gap: "8px", alignItems: "flex-start" }}>
      <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Activation Date
          </Typography>
          <Typography variant="body2">{stats.activationDate || "-"}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Recommendations
          </Typography>
          <Typography variant="body2">{stats.recommendations ?? 0}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Interview Requested
          </Typography>
          <Typography variant="body2">
            {stats.interviewsReceived ?? 0}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Interviews Accepted
          </Typography>
          <Typography variant="body2">
            {stats.interviewsAccepted ?? 0}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Booking Requested
          </Typography>
          <Typography variant="body2">{stats.bookingsReceived ?? 0}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Bookings Accepted
          </Typography>
          <Typography variant="body2">{stats.bookingsAccepted ?? 0}</Typography>
        </Box>
      </Box>
    </Stack>
  );
};

const TabMatchingCriteria = (props) => {
  const record = useRecordContext();
  const classes = useStyles();

  return (
    <Tab label="Matching" {...props}>
      <ShowContextProvider value={{ record }}>
        <Stack sx={{ display: "flex", flexDirection: "row" }}>
          <Stack sx={{ flexGrow: 1, gap: "8px", alignItems: "flex-start" }}>
            <Labeled label="Dogs OK?">
              <BooleanField source="allowsDogs" className={classes.inline} />
            </Labeled>

            <Labeled label="Cats OK?">
              <BooleanField source="allowsCats" className={classes.inline} />
            </Labeled>

            <Labeled label="Smoking OK?">
              <BooleanField source="allowsSmoking" className={classes.inline} />
            </Labeled>

            <Labeled label="Has Car?">
              <BooleanField source="hasCar" className={classes.inline} />
            </Labeled>

            <Labeled label="Has Special Needs Experience">
              <BooleanField
                source="hasSpecialNeedsExperience"
                className={classes.inline}
              />
            </Labeled>

            <Labeled label="Minimum Age">
              <TextField
                source="childcareAgeRange.min"
                className={classes.inline}
              />
            </Labeled>

            <Labeled label="Maximum Age">
              <TextField
                source="childcareAgeRange.max"
                className={classes.inline}
              />
            </Labeled>

            <Labeled label="Speaks Languages">
              <LanguageNameArrayField source="speaksLanguages" />
            </Labeled>

            <Labeled label="Reliability Score">
              <TextField source="reliabilityScore" className={classes.inline} />
            </Labeled>
          </Stack>

          <Stack sx={{ flexGrow: 1, gap: "8px", alignItems: "flex-start" }}>
            <Labeled label="Zipcode">
              <TextField source="address.zipcode" />
            </Labeled>

            <Labeled label="State">
              <TextField source="address.state" />
            </Labeled>

            <Labeled label="Commute time">
              <FunctionField
                render={({ desirableCommutingTime }) =>
                  desirableCommutingTime
                    ? `${desirableCommutingTime} minutes`
                    : "-"
                }
              />
            </Labeled>

            <OperationRadiusField />
            <GeolocationField />

            <Labeled label="Geolocation Link">
              <FunctionField
                render={({ geoLocation }) =>
                  geoLocation ? (
                    <a
                      target="blank"
                      href={`https://maps.google.com/?q=${geoLocation.lat},${geoLocation.lng}`}
                    >
                      See on the map
                    </a>
                  ) : (
                    "-"
                  )
                }
              />
            </Labeled>
          </Stack>
        </Stack>

        <Divider sx={{ margin: "8px 0" }} />

        <Stack sx={{ display: "flex", flexDirection: "row", gap: 4 }}>
          <Stack sx={{ flexGrow: 1 }}>
            <Labeled label="Availability">
              <AvailabilityField />
            </Labeled>
          </Stack>

          <Stack sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Matching now (90 days)
            </Typography>
            <ProviderMatchingStats />
          </Stack>
        </Stack>
      </ShowContextProvider>
    </Tab>
  );
};

export default TabMatchingCriteria;
